/**
 * src/nav/barras-rol.js — Barra por rol (SOA 10 PRO · fase 3, reordenada el 30-oct-2026).
 *
 * Mismos grupos y orden que el menú completo (8 grupos: Hoy · Mi trabajo · Operación · Cuentas · Equipo · Dirección y finanzas · Agentes y sistema ·
 * Administración); cada rol ve SOLO lo suyo. Nada se elimina: lo que no está en la barra sigue vivo (rutas intactas) y se llega con «Ver todo el OS»
 * (la elección se recuerda por usuario) o con el buscador (⌘K). Decisión del 2026-10-11: las entradas fusionadas o mudadas se OCULTAN, no se eliminan —
 * se decide con /api/nav/stats a los 60 días.
 *
 * Todo rol tiene barra (antes COM/SMM, CPC, IT y Comercial veían el menú completo). Tope de referencia: 24 entradas contando «Mi Día», que va suelto arriba;
 * lo verifica `npm run menu:auditar`. Cada barra incluye las pantallas nuevas que le tocan a su rol.
 *
 * Tokens especiales: "@cockpit" y "@bandeja" se resuelven con useRolNavigation (cada rol tiene el suyo; con ff_triage la bandeja es Triage).
 * Una entrada puede ser "/ruta" o ["/ruta", "Etiqueta"] para renombrar en esa barra.
 * Este archivo NO importa React ni íconos: es solo datos + una función pura.
 */

export const BARRAS = {
  creativos: {
    label: "Creativos",
    grupos: [
      ["Hoy", ["/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/horas", "/feedback", "/scorecard-rol", "/pizarra", "/academia", "/ayuda/tutoriales"]],
      ["Operación", ["/tareas", "/agenda", "/coberturas-v2", "/campo", "/generador-contenido"]],
    ],
  },
  ec: {
    label: "EC",
    grupos: [
      ["Hoy", ["@cockpit", "@bandeja", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/mi-cartera", "/scorecard-rol", "/pizarra"]],
      ["Operación", ["/tareas", "/agenda", "/feed-preview", "/pedidos-cliente", "/pedidos-agencia", "/briefs-plan"]],
      ["Cuentas", ["/clientes", "/cartera", "/reportes", "/reportes-clientes", "/banco-recursos"]],
      ["Agentes y sistema", ["/casos"]],
    ],
  },
  ccc: {
    label: "CCC",
    grupos: [
      ["Hoy", ["@cockpit", "@bandeja", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1"]],
      ["Operación", ["/tareas", "/agenda", "/feed-preview", "/pedidos-cliente", "/coberturas-v2"]],
      ["Cuentas", ["/portal-inbox"]],
      ["Equipo", ["/asignacion", "/apelaciones", "/equipo/dashboard", "/equipo/cierre-dia"]],
    ],
  },
  com: {
    label: "COM y SMM",
    grupos: [
      ["Hoy", ["@cockpit", "@bandeja", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/mis-cuentas", "/pizarra", "/scorecard-rol"]],
      ["Operación", ["/tareas", "/agenda", "/feed-preview", "/publicacion", "/briefs-plan", "/generador-contenido", "/pedidos-cliente"]],
      ["Cuentas", ["/clientes", "/banco-recursos", "/calidad/cliente", "/reportes"]],
      ["Agentes y sistema", ["/casos"]],
    ],
  },
  cpc: {
    label: "CPC",
    grupos: [
      ["Hoy", ["@cockpit", "@bandeja", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/horas"]],
      ["Operación", ["/coberturas-v2", "/coberturas/control", "/campo", "/agenda", "/tareas", "/live-events", "/producciones-terceros"]],
      ["Cuentas", ["/clientes", "/partners", "/proveedores"]],
      ["Equipo", ["/especialistas-dashboard", "/carga-forecast"]],
      ["Agentes y sistema", ["/casos"]],
    ],
  },
  comercial: {
    label: "Comercial",
    grupos: [
      ["Hoy", ["@cockpit", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1"]],
      ["Cuentas", ["/leads", "/clientes", "/partners", "/reportes"]],
    ],
  },
  it: {
    label: "IT",
    grupos: [
      ["Hoy", ["@cockpit", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/ayuda/tutoriales"]],
      ["Agentes y sistema", ["/agentes", "/it-lead", "/casos"]],
      ["Administración", [["/admin/settings", "Configuración"], "/usuarios-evento", "/ai-cost", "/audit"]],
    ],
  },
  ceo_coo: {
    label: "CEO y COO",
    grupos: [
      ["Hoy", ["@cockpit", "@bandeja", "/novedades", "/chat"]],
      ["Operación", ["/tareas", "/agenda", "/coberturas-v2"]],
      ["Cuentas", ["/clientes", "/cartera", "/reportes", "/pizarra"]],
      ["Equipo", ["/equipo", "/carga-forecast", "/asignador"]],
      ["Dirección y finanzas", ["/cockpit", "/okrs", "/fee-ledger", "/facturacion"]],
      ["Agentes y sistema", ["/casos", "/agentes", "/transicion"]],
      ["Administración", [["/admin/settings", "Configuración"]]],
    ],
  },
  cfo: {
    label: "CFO",
    grupos: [
      ["Hoy", ["@cockpit", "/novedades", "/chat"]],
      ["Mi trabajo", ["/tareas?mis=1", "/ayuda/tutoriales"]],
      ["Cuentas", ["/reportes"]],
      ["Dirección y finanzas", ["/dashboard", "/proyectos", "/fee-ledger", "/cockpit/md", "/admin/financiero", "/facturacion", "/admin/cobranzas", "/costos-operativos", "/costeo", "/viaticos"]],
    ],
  },
};

/**
 * Qué barra le toca al usuario (null = sin barra: ve el menú completo). Los roles son aditivos (una persona puede ser COO y Diseñador): manda el de
 * más responsabilidad, en este orden. Todo rol conocido tiene barra.
 */
export function barraDeUsuario({ isAdmin, isCOO, isCFO, isCCC, isCuenta, isCPC, isCM, isIT, isHunter, isEspecialista } = {}) {
  if (isAdmin || isCOO) return "ceo_coo";
  if (isCFO) return "cfo";
  if (isCCC) return "ccc";
  if (isCuenta) return "ec";
  if (isCPC) return "cpc";
  if (isCM) return "com";
  if (isIT) return "it";
  if (isHunter) return "comercial";
  if (isEspecialista) return "creativos";
  return null;
}

const base = (to) => String(to || "").split("?")[0];

/**
 * Aplica una barra al menú completo: conserva el orden de la barra, resuelve los tokens
 * y devuelve [{ label, items }] con los items reales del registro (iconos, badges, scope).
 * Las rutas que no existan en el registro se omiten (nunca se inventa una entrada).
 */
export function aplicarBarra(gruposCompletos, barra, nav = {}) {
  if (!barra) return gruposCompletos;
  const todos = gruposCompletos.flatMap(g => g.items);
  const porRutaExacta = new Map(todos.map(it => [it.to, it]));
  const buscar = (token) => {
    if (token === "@cockpit") return porRutaExacta.get(nav.miCockpit);
    if (token === "@bandeja") return porRutaExacta.get(nav.miBandeja);
    return porRutaExacta.get(token) || todos.find(it => base(it.to) === base(token) && !String(token).includes("?"));
  };
  return barra.grupos
    .map(([label, entradas]) => ({
      label,
      items: entradas
        .map(e => {
          const [token, etiqueta] = Array.isArray(e) ? e : [e, null];
          const it = buscar(token);
          return it ? (etiqueta ? { ...it, label: etiqueta } : it) : null;
        })
        .filter(Boolean),
    }))
    .filter(g => g.items.length > 0);
}

/** Cantidad de entradas de una barra, contando «Mi Día». */
export const totalBarra = (barra) => 1 + barra.grupos.reduce((n, [, e]) => n + e.length, 0);
